// @ts-nocheck
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Homework, HomeworkTask, HomeworkTaskType, Submission, Student, Assignment, GradingSystem } from '../../types';
import Card from './Card';
import { XMarkIcon, CheckIcon, CheckBadgeIcon, ClockIcon, TrophyIcon, ClipboardDocumentListIcon, ChatBubbleBottomCenterTextIcon, PencilIcon, PaperClipIcon, SparklesIcon, ArrowPathIcon, InformationCircleIcon, CheckCircleIcon } from './Icons';
import { formatAssetUrl, aiGradeSubmissionApi } from '../../services/apiService';
import Spinner from './Spinner';
import { formatGrade, isSubmissionGraded } from '../../utils/assignmentUtils';
import { getMcqCorrectAnswerTexts } from '../../utils/homeworkMcq';

interface GradingModalProps {
    homework: Homework;
    submission: Submission;
    student: Student;
    assignment?: Assignment;
    onClose: () => void;
    onSaveGrade: (submissionId: string, grade: number, teacherComments: string) => void;
}

const GradingModal: React.FC<GradingModalProps> = ({ homework, submission, student, assignment, onClose, onSaveGrade }) => {
    const [grade, setGrade] = useState<number>(submission.grade || 0);
    const [gradingSystem, setGradingSystem] = useState<GradingSystem>(assignment?.gradingSystem || 'numeric');
    const [teacherComments, setTeacherComments] = useState<string>(submission.teacherComments || '');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [taskGrades, setTaskGrades] = useState<Record<string, boolean | number>>({});
    const [isAiGrading, setIsAiGrading] = useState(false);
    const [aiFeedback, setAiFeedback] = useState<Record<string, string>>({});

    const rawContent = submission.content;
    const parsedContent = typeof rawContent === 'string' ? (() => { try { return JSON.parse(rawContent); } catch { return {}; } })() : (rawContent || {});
    const answers = parsedContent.answers || {};
    const tasks = homework.tasks || (homework as any).task_list || [];

    const getAnswerForTask = useCallback((task: HomeworkTask, taskIndex: number): any => {
        const direct = answers[task.id];
        if (direct !== undefined && direct !== null) return direct;
        const answerKeys = Object.keys(answers).sort();
        if (answerKeys.length === 0) return undefined;
        const keyByTaskId = answerKeys.find(k => String(k).toLowerCase() === String(task.id).toLowerCase());
        if (keyByTaskId !== undefined) return answers[keyByTaskId];
        const interactiveTasks = tasks.filter(t => t.taskType !== HomeworkTaskType.Instruction);
        const interactiveIdx = interactiveTasks.findIndex(t => t.id === task.id);
        if (interactiveIdx >= 0 && interactiveIdx < answerKeys.length) return answers[answerKeys[interactiveIdx]];
        return undefined;
    }, [answers, tasks]);

    // Auto-calculate suggested grade
    useEffect(() => {
        const initialTaskGrades: Record<string, boolean | number> = {};
        let correctCount = 0;
        let interactiveTaskCount = 0;

        tasks.forEach((task, idx) => {
            if (task.taskType === HomeworkTaskType.Instruction) return;
            interactiveTaskCount++;
            const studentAns = getAnswerForTask(task, idx);
            const isCorrect = calculateScoreForTask(task, studentAns);
            initialTaskGrades[task.id] = isCorrect;
            if (isCorrect === true) correctCount++;
        });

        setTaskGrades(initialTaskGrades);
        
        // Only set initial grade if not already graded
        if (!isSubmissionGraded(submission.status) && interactiveTaskCount > 0) {
            const suggestedGrade = Math.round((correctCount / interactiveTaskCount) * 100) / 10;
            setGrade(suggestedGrade);
        }
    }, [tasks, answers, submission.status, getAnswerForTask]);

    const handleTaskGradeChange = (taskId: string, isCorrect: boolean) => {
        const newTaskGrades = { ...taskGrades, [taskId]: isCorrect };
        setTaskGrades(newTaskGrades);
        
        // Recalculate total grade
        const interactiveTasks = tasks.filter(t => t.taskType !== HomeworkTaskType.Instruction);
        let correctCount = 0;
        interactiveTasks.forEach(t => {
            if (newTaskGrades[t.id] === true) correctCount++;
        });
        
        if (interactiveTasks.length > 0) {
            setGrade(Math.round((correctCount / interactiveTasks.length) * 100) / 10);
        }
    };

    const handleAiGrade = async () => {
        if (!window.confirm('Hệ thống sẽ sử dụng AI để phân tích bài làm (đặc biệt là các câu tự luận) dựa trên tiêu chí chấm điểm.\n\nChi phí: trừ theo số token AI tiêu tốn. Bạn có muốn tiếp tục?')) return;
        
        setIsAiGrading(true);
        try {
            const result = await aiGradeSubmissionApi(submission.id);
            if (result) {
                setGrade(result.suggestedGrade);
                setTeacherComments(result.teacherComments);
                
                if (result.taskResults) {
                    const newTaskGrades = { ...taskGrades };
                    const newAiFeedback = { ...aiFeedback };
                    Object.entries(result.taskResults).forEach(([taskId, res]) => {
                        newTaskGrades[taskId] = res.isCorrect;
                        newAiFeedback[taskId] = res.feedback;
                    });
                    setTaskGrades(newTaskGrades);
                    setAiFeedback(newAiFeedback);
                }
                
                const msg = result.creditsDeducted != null
                    ? `Đã chấm điểm thành công! AI gợi ý ${result.suggestedGrade} điểm. Đã trừ ${result.creditsDeducted} tín dụng (theo token sử dụng).`
                    : `Đã chấm điểm thành công! AI đã phân tích và gợi ý ${result.suggestedGrade} điểm.`;
                alert(msg);
            }
        } catch (err) {
            alert('Lỗi khi chấm bài AI: ' + (err.message || 'Vui lòng thử lại sau.'));
        } finally {
            setIsAiGrading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-5xl h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white/80 backdrop-blur-md sticky top-0 z-20">
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-[1.25rem] bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-sky-100 shrink-0">
                            <CheckBadgeIcon className="h-7 w-7" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Chấm điểm bài làm</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="px-2 py-0.5 bg-sky-50 text-[10px] font-black text-sky-600 rounded-md uppercase tracking-tighter">Học sinh</span>
                                <p className="text-sm text-slate-600 font-bold">
                                    {student?.name || submission.studentId || (submission as any).student_id || 'Ẩn danh'}
                                </p>
                                <span className="text-slate-300 mx-1">•</span>
                                <p className="text-sm text-slate-400 font-medium truncate max-w-[300px] italic">
                                    {homework.title}
                                </p>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="p-3 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-2xl transition-all active:scale-90"
                    >
                        <XMarkIcon className="h-7 w-7" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Left side: Student's Work */}
                        <div className="lg:col-span-8 space-y-8">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    <ClipboardDocumentListIcon className="h-4 w-4" /> Chi tiết bài làm
                                </h3>
                                {submission.submittedDate && (
                                    <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-slate-100 rounded-full shadow-sm text-[10px] font-bold text-slate-400">
                                        <ClockIcon className="h-3 w-3" />
                                        Nộp lúc: {new Date(submission.submittedDate).toLocaleString('vi-VN')}
                                    </div>
                                )}
                            </div>

                            {/* Teacher's Reference / Instructions */}
                            {(assignment?.instructions || (assignment?.attachments && assignment.attachments.length > 0)) && (
                                <Card className="p-5 border-none shadow-sm bg-amber-50/30 border border-amber-100 rounded-[2rem] overflow-hidden relative group/ref transition-all hover:bg-amber-50/50">
                                    <div className="absolute top-0 right-0 w-24 h-24 -mr-10 -mt-10 bg-amber-100/30 rounded-full blur-2xl group-hover/ref:scale-110 transition-transform" />
                                    <div className="flex items-center gap-3 mb-3 relative z-10">
                                        <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                                            <InformationCircleIcon className="h-4 w-4" />
                                        </div>
                                        <h3 className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Nội dung & Tài liệu tham chiếu của giáo viên</h3>
                                    </div>
                                    
                                    {assignment?.instructions && (
                                        <div className="relative z-10 bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-amber-100/50 mb-3 shadow-sm">
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed font-medium italic">"{assignment.instructions}"</p>
                                        </div>
                                    )}

                                    {assignment?.attachments && assignment.attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2 relative z-10">
                                            {assignment.attachments.map((att, i) => (
                                                <button 
                                                    key={i} 
                                                    onClick={() => window.open(formatAssetUrl(att.url), '_blank')}
                                                    className="px-3 py-2 bg-white/80 hover:bg-white text-slate-600 rounded-xl text-[10px] font-black border border-amber-100 flex items-center gap-2 hover:text-amber-700 hover:border-amber-300 transition-all shadow-sm active:scale-95"
                                                >
                                                    <span>{att.type === 'image' ? '🖼' : att.type === 'video' ? '🎬' : att.type === 'audio' ? '🎵' : '📄'}</span>
                                                    <span className="truncate max-w-[180px]">{att.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            )}
                            
                            {tasks.map((task, index) => {
                                const studentAnswer = getAnswerForTask(task, index);
                                const isCorrect = taskGrades[task.id];
                                const isInstruction = task.taskType === HomeworkTaskType.Instruction;

                                return (
                                    <Card key={task.id} className={`p-6 border-none shadow-sm relative overflow-hidden transition-all ${!isInstruction && (isCorrect === true ? 'ring-2 ring-emerald-500/20' : isCorrect === false ? 'ring-2 ring-rose-500/20' : '')}`}>
                                        <div className="flex gap-4">
                                            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 font-bold text-sm">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1 space-y-4">
                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                            {task.taskType}
                                                        </span>
                                                        {!isInstruction && (
                                                            <div className="flex gap-1">
                                                                <button 
                                                                    onClick={() => handleTaskGradeChange(task.id, true)}
                                                                    className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${isCorrect === true ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-slate-100 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'}`}
                                                                >
                                                                    ĐÚNG
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleTaskGradeChange(task.id, false)}
                                                                    className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${isCorrect === false ? 'bg-rose-500 text-white shadow-lg shadow-rose-100' : 'bg-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-600'}`}
                                                                >
                                                                    SAI
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className="text-base font-bold text-slate-800 leading-snug">{task.description}</p>
                                                </div>

                                                {task.imageUrl && (
                                                    <div className="rounded-xl overflow-hidden border bg-gray-50 shadow-sm max-w-sm">
                                                        <img src={formatAssetUrl(task.imageUrl)} alt={task.description} className="w-full h-auto" />
                                                    </div>
                                                )}

                                                {/* Student Answer and Correction */}
                                                {!isInstruction && (
                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                            <div className="space-y-2">
                                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bài làm của học sinh</p>
                                                                <div className={`p-4 rounded-2xl border-2 transition-all ${isCorrect === true ? 'bg-emerald-50/50 border-emerald-100' : isCorrect === false ? 'bg-rose-50/50 border-rose-100' : 'bg-slate-50 border-slate-100'}`}>
                                                                    {renderAnswerContent(task, studentAnswer)}
                                                                </div>
                                                            </div>
                                                        <div className="space-y-2">
                                                            <p className="text-[10px] font-black text-sky-500 uppercase tracking-widest">Đáp án đúng / Hướng dẫn & Tiêu chí chấm</p>
                                                            <div className="p-4 rounded-2xl bg-sky-50/50 border-2 border-sky-100">
                                                                {renderCorrectContent(task)}
                                                            </div>
                                                        </div>
                                                        </div>
                                                        {aiFeedback[task.id] && (
                                                            <div className="p-4 bg-teal-50/30 rounded-2xl border border-teal-100 flex gap-3 animate-in slide-in-from-top-2 duration-500">
                                                                <div className="shrink-0 w-8 h-8 rounded-xl bg-teal-100 flex items-center justify-center text-teal-600">
                                                                    <SparklesIcon className="h-4 w-4" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-[10px] font-black text-teal-700 uppercase tracking-widest mb-1">AI Phân tích & Nhận xét</p>
                                                                    <p className="text-xs text-slate-600 font-medium italic">{aiFeedback[task.id]}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Right side: Grading Controls */}
                        <div className="lg:col-span-4 relative">
                            <div className="sticky top-0 z-20 pt-2 lg:pt-0">
                                <Card className="p-4 sm:p-5 border-none shadow-2xl shadow-slate-200/50 bg-white rounded-[2rem] ring-1 ring-slate-100 space-y-4">
                                    <div className="flex items-center justify-between gap-3 border-b border-slate-50 pb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 border border-teal-100 shrink-0">
                                                <TrophyIcon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Kết quả</h3>
                                                <div className="flex items-baseline gap-1">
                                                    {gradingSystem === 'none' ? (
                                                        <span className="text-sm font-black text-teal-600">KHÔNG CHẤM</span>
                                                    ) : (
                                                        <>
                                                            <input 
                                                                type="number" 
                                                                min="0" max="10" step="0.1"
                                                                value={grade}
                                                                onChange={(e) => { setGrade(parseFloat(e.target.value)); setErrors(prev => ({ ...prev, grade: '' })); }}
                                                                className={`w-16 p-0 text-2xl font-black bg-transparent border-none outline-none focus:ring-0 transition-all ${errors.grade ? 'text-rose-600' : 'text-slate-800'}`}
                                                            />
                                                            <span className="text-[10px] font-bold text-slate-400">/ 10</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <button 
                                            onClick={handleAiGrade}
                                            disabled={isAiGrading}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl text-[10px] font-black shadow-lg shadow-teal-100 hover:shadow-xl hover:scale-105 transition-all active:scale-95 disabled:opacity-50"
                                        >
                                            {isAiGrading ? <Spinner size="xs" color="white" /> : <SparklesIcon className="h-3 w-3" />}
                                            <span>{isAiGrading ? '...' : (gradingSystem === 'none' ? 'AI GỢI Ý' : 'AI CHẤM')}</span>
                                        </button>
                                    </div>

                                    {/* Grading System Display */}
                                    {gradingSystem !== 'none' && (
                                        <div className="bg-slate-50/80 backdrop-blur-sm p-3 rounded-2xl flex items-center justify-between border border-slate-100">
                                            <div className="grid grid-cols-3 gap-1 flex-1">
                                                {[
                                                    { id: 'numeric', label: 'Hệ 10' },
                                                    { id: 'letter', label: 'Hệ chữ' },
                                                    { id: 'pass_fail', label: 'Đạt/K' }
                                                ].map(opt => (
                                                    <button 
                                                        key={opt.id}
                                                        onClick={() => setGradingSystem(opt.id as any)} 
                                                        className={`py-1.5 px-1 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all ${
                                                            gradingSystem === opt.id 
                                                            ? 'bg-white text-sky-600 shadow-sm ring-1 ring-sky-100' 
                                                            : 'text-slate-400 hover:text-slate-600'
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="ml-3 pl-3 border-l border-slate-200">
                                                <div className="px-2 py-1 bg-white rounded-lg border border-slate-100 text-[11px] font-black text-sky-600 shadow-sm">
                                                    {formatGrade(grade, gradingSystem)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Comments Area */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 px-1">
                                            <ChatBubbleBottomCenterTextIcon className="h-3.5 w-3.5 text-slate-400" />
                                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                                {gradingSystem === 'none' ? 'Lời khuyên & Động viên' : 'Lời phê giáo viên'}
                                            </h4>
                                        </div>
                                        <div className="relative group/comment">
                                            <textarea 
                                                value={teacherComments}
                                                onChange={(e) => setTeacherComments(e.target.value)}
                                                className="w-full h-28 sm:h-32 p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-sky-500/10 focus:border-sky-500 focus:bg-white transition-all text-xs sm:text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-inner resize-none outline-none"
                                                placeholder="Nhập lời khuyên hoặc nhận xét..."
                                            />
                                            <div className="absolute bottom-3 right-3 text-[9px] font-black text-slate-300 group-focus-within/comment:text-sky-400 transition-colors uppercase tracking-widest">
                                                {teacherComments.length}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className="pt-2">
                                        <button 
                                            onClick={() => {
                                                if (isNaN(grade) || grade < 0 || grade > 10) {
                                                    setErrors({ grade: 'Điểm số phải từ 0 đến 10' });
                                                    return;
                                                }
                                                onSaveGrade(submission.id, grade, teacherComments);
                                                onClose();
                                            }}
                                            className="w-full py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-2xl font-black text-xs sm:text-sm shadow-xl shadow-sky-200 hover:shadow-2xl hover:shadow-sky-300 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 group"
                                        >
                                            <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:scale-110" />
                                            <span>XÁC NHẬN & LƯU</span>
                                        </button>
                                    </div>
                                    
                                    {/* Additional info if space allowed, otherwise keep it simple */}
                                    {submission.content?.attachments?.length > 0 && (
                                        <div className="space-y-2 pt-2 border-t border-slate-50">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                <PaperClipIcon className="h-3 w-3 text-sky-500" /> Tệp đính kèm
                                            </label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {submission.content.attachments.map((att, i) => (
                                                    <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg text-[9px] font-bold text-sky-600 hover:text-sky-700 border border-sky-100 max-w-full">
                                                        <PaperClipIcon className="h-2.5 w-2.5" />
                                                        <span className="truncate max-w-[100px]">{att.name}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper function to render student answer content
const renderAnswerContent = (task: HomeworkTask, answer: any) => {
    if (answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && answer.trim() === '')) {
        return <p className="text-slate-400 italic text-sm">Học sinh chưa trả lời câu hỏi này.</p>;
    }

    switch (task.taskType) {
        case HomeworkTaskType.MultipleChoiceSingle:
        case HomeworkTaskType.TrueFalse:
            return <p className="font-bold text-slate-700">{answer}</p>;
        case HomeworkTaskType.MultipleChoiceMulti:
            return (
                <div className="flex flex-wrap gap-2">
                    {answer.map((a: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-white rounded-lg border border-slate-200 text-xs font-bold">{a}</span>
                    ))}
                </div>
            );
        case HomeworkTaskType.Matching:
            const answerPairs = Array.isArray(answer) ? answer : [];
            return (
                <div className="space-y-1">
                    {answerPairs.map((pair: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="font-bold text-slate-700">{pair?.left ?? pair}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-bold text-slate-700">{pair?.right ?? pair}</span>
                        </div>
                    ))}
                </div>
            );
        case HomeworkTaskType.Ordering:
            return (
                <div className="space-y-1">
                    {answer.map((item: string, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-bold">
                            <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px]">{i + 1}</span>
                            {item}
                        </div>
                    ))}
                </div>
            );
        case HomeworkTaskType.FillInBlanks:
            return (
                <div className="flex flex-wrap gap-2">
                    {answer.map((a: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-white rounded-lg border border-slate-200 text-xs font-black text-sky-600">[{i + 1}] {a}</span>
                    ))}
                </div>
            );
        case HomeworkTaskType.Writing:
        case HomeworkTaskType.Questions:
        case HomeworkTaskType.Research:
            return <p className="text-sm font-medium text-slate-700 whitespace-pre-line leading-relaxed">{answer}</p>;
        default:
            return <p className="text-sm font-bold text-slate-700">{JSON.stringify(answer)}</p>;
    }
};

// Helper function to render correct answer / guide
const renderCorrectContent = (task: HomeworkTask) => {
    const content = task.content;
    if (!content) return <p className="text-slate-400 italic text-sm">Không có đáp án mẫu.</p>;

    switch (task.taskType) {
        case HomeworkTaskType.MultipleChoiceSingle: {
            const texts = getMcqCorrectAnswerTexts(content, content.options || []);
            const t = texts[0];
            return <p className="font-bold text-emerald-600">{t != null ? t : content.correctAnswer}</p>;
        }
        case HomeworkTaskType.TrueFalse:
            return <p className="font-bold text-emerald-600">{content.correctAnswer}</p>;
        case HomeworkTaskType.MultipleChoiceMulti: {
            const texts = getMcqCorrectAnswerTexts(content, content.options || []);
            const arr = texts.length ? texts : (Array.isArray(content.correctAnswer) ? content.correctAnswer : [content.correctAnswer]);
            return (
                <div className="flex flex-wrap gap-2">
                    {arr.filter(Boolean).map((a: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-emerald-50 rounded-lg border border-emerald-100 text-xs font-bold text-emerald-700">{a}</span>
                    ))}
                </div>
            );
        }
        case HomeworkTaskType.Matching:
            const pairsArr = Array.isArray(content.pairs) ? content.pairs : (content.pairs && typeof content.pairs === 'object' ? Object.values(content.pairs) : []);
            return (
                <div className="space-y-1">
                    {pairsArr.map((pair: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="font-bold text-emerald-700">{pair?.left ?? pair}</span>
                            <span className="text-emerald-300">→</span>
                            <span className="font-bold text-emerald-700">{pair?.right ?? pair}</span>
                        </div>
                    ))}
                </div>
            );
        case HomeworkTaskType.Ordering:
            return (
                <div className="space-y-1">
                    {content.orderedItems?.map((item: string, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-bold text-emerald-700">
                            <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-[10px]">{i + 1}</span>
                            {item}
                        </div>
                    ))}
                </div>
            );
        case HomeworkTaskType.FillInBlanks:
            return (
                <div className="flex flex-wrap gap-2">
                    {content.blanks?.map((a: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-emerald-50 rounded-lg border border-emerald-100 text-xs font-black text-emerald-700">[{i + 1}] {a}</span>
                    ))}
                </div>
            );
        case HomeworkTaskType.Writing:
        case HomeworkTaskType.Questions:
        case HomeworkTaskType.Research:
            return <p className="text-sm font-medium text-slate-600 italic whitespace-pre-line leading-relaxed">{content.answerGuide || 'Chấm điểm dựa trên nội dung trả lời.'}</p>;
        default:
            return content.correctAnswer ? <p className="font-bold text-emerald-600">{content.correctAnswer}</p> : <p className="text-slate-400 italic text-sm">Xem hướng dẫn chấm.</p>;
    }
};

// Logic to check if student answer is correct
const calculateScoreForTask = (task: HomeworkTask, studentAnswer: any): boolean | null => {
    if (!task.content || studentAnswer === undefined || studentAnswer === null) return null;
    
    switch (task.taskType) {
        case HomeworkTaskType.MultipleChoiceSingle: {
            const texts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
            return texts.length === 1 && String(studentAnswer) === texts[0];
        }
        case HomeworkTaskType.TrueFalse:
            return studentAnswer === task.content.correctAnswer;
        case HomeworkTaskType.MultipleChoiceMulti: {
            const texts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
            const correctSet = new Set(texts.map(String));
            const studentSet = new Set(Array.isArray(studentAnswer) ? studentAnswer.map(String) : []);
            if (correctSet.size !== studentSet.size) return false;
            return [...correctSet].every(val => studentSet.has(val));
        }
        case HomeworkTaskType.Matching:
            const rawPairs = task.content.pairs;
            const correctPairs = Array.isArray(rawPairs) ? rawPairs : (rawPairs && typeof rawPairs === 'object' ? Object.values(rawPairs) : []);
            const studentPairs = studentAnswer || [];
            if (correctPairs.length === 0) return null;
            if (correctPairs.length !== studentPairs.length) return false;
            return correctPairs.every(cp => studentPairs.some((sp: any) => sp?.left === cp?.left && sp?.right === cp?.right));
        case HomeworkTaskType.Ordering:
            const correctOrder = task.content.orderedItems || [];
            const studentOrder = studentAnswer || [];
            if (correctOrder.length === 0) return null;
            if (correctOrder.length !== studentOrder.length) return false;
            return correctOrder.every((item, i) => item === studentOrder[i]);
        case HomeworkTaskType.FillInBlanks:
            const correctBlanks = task.content.blanks || [];
            const studentBlanks = studentAnswer || [];
            if (correctBlanks.length === 0) return null;
            return correctBlanks.every((b, i) => b.toLowerCase().trim() === (studentBlanks[i] || '').toLowerCase().trim());
        case HomeworkTaskType.Writing:
        case HomeworkTaskType.Questions:
        case HomeworkTaskType.Research:
            return null; // Manual grading required
        default:
            return null;
    }
};

export default GradingModal;
